'use client'

import { useEffect, useMemo, useRef } from 'react'
import { Fraction, withoutScientificNotation } from 'sushi'
import type { EvmChainId } from 'sushi/evm'
import { parseUnits } from 'viem'
import { usePriceProvider } from './price-provider'

export type PriceMap<TChainId extends EvmChainId = EvmChainId> = {
  has: (address: AddressFor<TChainId>) => boolean
  get: (address: AddressFor<TChainId>) => number | undefined
  getFraction: (address: AddressFor<TChainId>) => Fraction | undefined
}

type UsePricesResult<TChainId extends EvmChainId> = {
  data: PriceMap<TChainId> | undefined
  lastModified: number
  isLoading: boolean
  isUpdating: boolean
  isError: boolean
}

const disabledData = {
  data: undefined,
  lastModified: 0,
  isLoading: false,
  isUpdating: false,
  isError: false,
}

function translateAddress<TChainId extends EvmChainId>(
  _chainId: TChainId,
  address: AddressFor<TChainId>,
): bigint {
  return BigInt(address)
}

export function usePrices<TChainId extends EvmChainId>({
  chainId,
  enabled = true,
}: {
  chainId: TChainId | undefined
  enabled?: boolean
}): UsePricesResult<TChainId> {
  const { state, mutate } = usePriceProvider()

  useEffect(() => {
    if (state.ready && chainId && enabled) {
      mutate.incrementChainId(chainId)
    }

    return () => {
      if (state.ready && chainId && enabled) {
        mutate.decrementChainId(chainId)
      }
    }
  }, [chainId, enabled, mutate, state.ready])

  const chain = useMemo(
    () => (chainId ? state.chains.get(chainId) : undefined),
    [state, chainId],
  )

  return useMemo(() => {
    if (!chainId) {
      return disabledData
    }

    if (!chain)
      return {
        data: undefined,
        lastModified: 0,
        isLoading: true,
        isUpdating: false,
        isError: false,
      }

    if (!chain.priceMap)
      return {
        data: undefined,
        lastModified: chain.lastModified,
        isLoading: chain.isLoading,
        isUpdating: chain.isUpdating,
        isError: chain.isError,
      }

    const data: PriceMap<TChainId> = {
      has: (_address: AddressFor<TChainId>) => {
        const address = translateAddress(chainId, _address)
        return chain.priceMap!.has(address)
      },
      get: (_address: AddressFor<TChainId>) => {
        const address = translateAddress(chainId, _address)
        return chain.priceMap!.get(address)
      },

      getFraction: (_address: AddressFor<TChainId>) => {
        const address = translateAddress(chainId, _address)

        const price = chain.priceMap!.get(address)
        if (price) {
          return new Fraction({
            numerator: parseUnits(
              withoutScientificNotation(String(price)) || '0',
              18,
            ).toString(),
            denominator: parseUnits('1', 18).toString(),
          })
        }
        return undefined
      },
    }

    return {
      data,
      lastModified: chain.lastModified,
      isLoading: chain.isLoading,
      isUpdating: chain.isUpdating,
      isError: chain.isError,
    }
  }, [chainId, chain])
}
